import { useEffect, useState } from "react"
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom"
import { getAssessmentSession, createDevShortcutSession } from "~/api/assessmentSessions"
import type { AssessmentSessionResponse } from "~/api/assessmentSessions"
import type { LayoutOutletContext } from "~/components/Layout"
import { CharacterCard } from "~/components/CharacterCard"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"

export function ResultsPage() {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { profile, authLoading } = useOutletContext<LayoutOutletContext>()
  const state = location.state as {
    sessionToken?: string
    result?: AssessmentSessionResponse
    addDog?: boolean
  } | null
  const [result, setResult] = useState<AssessmentSessionResponse | null>(state?.result ?? null)
  const tokenFromUrl = searchParams.get("token")
  const devTypeOnly = import.meta.env.DEV && !state?.result && !(state?.sessionToken ?? tokenFromUrl) && searchParams.get("type")
  const [loading, setLoading] = useState(
    !result && (!!(state?.sessionToken ?? tokenFromUrl) || !!devTypeOnly)
  )
  const [error, setError] = useState<string | null>(null)
  const authReady = !authLoading
  const isAuthenticated = profile !== null
  const isAddDog = Boolean(state?.addDog)
  useEffect(() => {
    if (result) return
    const token = state?.sessionToken ?? tokenFromUrl ?? null
    if (!token) {
      const devType = import.meta.env.DEV ? searchParams.get("type") : null
      if (devType) {
        createDevShortcutSession(devType, searchParams.get("name") ?? undefined)
          .then((res) => {
            setResult(res)
          })
          .catch(() => setError("Could not load results"))
          .finally(() => setLoading(false))
        return
      }
      setError("No results to show")
      setLoading(false)
      return
    }
    getAssessmentSession(token)
      .then(setResult)
      .catch(() => setError("Could not load results"))
      .finally(() => setLoading(false))
  }, [result, state?.sessionToken, tokenFromUrl, searchParams])

  if (loading) {
    return (
      <PageShell>
        <p className="pt-4 text-[var(--color-text-secondary)]">Loading results...</p>
      </PageShell>
    )
  }

  if (error || !result) {
    return (
      <PageShell>
        <p className="pt-4 text-red-600" role="alert">
          {error ?? "No results to show"}
        </p>
        <Link to="/" className="text-[var(--color-text-primary)] underline">
          Start over
        </Link>
      </PageShell>
    )
  }

  const sessionToken = state?.sessionToken ?? new URLSearchParams(location.search).get("token")
  const archetype = result.archetype

  if (!archetype) {
    return (
      <PageShell>
        <p className="pt-4 text-[var(--color-text-secondary)]">No archetype data.</p>
      </PageShell>
    )
  }

  if (!authReady) {
    return (
      <PageShell>
        <p className="pt-4 text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  return (
    <PageShell
      innerClassName={isAuthenticated ? "max-w-lg w-full" : undefined}
      fixedTabNav={isAuthenticated}
    >
      {isAuthenticated ? (
        <LoggedInScreenCard>
          <CharacterCard
            dogData={result}
            sessionToken={sessionToken}
            isAuthenticated
            portrait={profile?.dog_profile?.portrait ?? null}
          />
          {isAddDog && (
            <div className="mt-6 space-y-3 px-2 text-center">
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                {result.dog_name} has joined your pack!
              </p>
              <Link
                to="/dashboard"
                className="inline-flex w-full justify-center rounded-lg bg-[var(--color-brand-primary)] px-4 py-3 text-sm font-semibold text-white hover:opacity-90"
              >
                Continue to {result.dog_name}&apos;s Dashboard
              </Link>
            </div>
          )}
        </LoggedInScreenCard>
      ) : (
        <CharacterCard dogData={result} sessionToken={sessionToken} />
      )}
    </PageShell>
  )
}
